import { PAGINATION_LIMIT, RESOURCE } from '@src/constants';
import { DUBBING_URL } from '@src/configs';
import { EXPORT_TYPE } from '@src/constants/presentation';
import { omitIsNil } from '@src/utils/omit';
import api from './api';

export const getProjects = async (
  { search, offset, limit = PAGINATION_LIMIT, fields, sort },
  options,
) => {
  const params = omitIsNil({
    search,
    offset,
    limit,
    fields,
    sort,
  });
  const response = await api({
    method: 'GET',
    url: RESOURCE.PROJECTS,
    params,
    ...options,
  });
  return response;
};

export const createProject = async (data) => {
  const response = await api({
    method: 'POST',
    url: RESOURCE.PROJECTS,
    data,
  });
  return response;
};

export const deleteProject = async (projectId) => {
  const response = await api({
    method: 'DELETE',
    url: `${RESOURCE.PROJECTS}/${projectId}`,
  });
  return response;
};

export const deleteManyProjects = async (projectIds) => {
  const response = await api({
    method: 'DELETE',
    url: `${RESOURCE.PROJECTS}`,
    data: { projectIds },
  });
  return response;
};

export const updateProject = async (projectId, data) => {
  const response = await api({
    method: 'PUT',
    url: `${RESOURCE.PROJECTS}/${projectId}`,
    data,
  });
  return response;
};

export const getProject = async (projectId, fields) => {
  const response = await api({
    method: 'GET',
    url: `${RESOURCE.PROJECTS}/${projectId}`,
    params: { fields },
  });

  return response;
};

export const getJoinedAudio = async (projectId, mode) => {
  const response = await api({
    method: 'GET',
    url: `${RESOURCE.PROJECTS}/${projectId}/audio?mode=${mode}`,
  });

  return response;
};

export const getSampleProject = async () => {
  const response = await api({
    method: 'GET',
    url: `${RESOURCE.PROJECTS}/sample`,
  });

  return response;
};

export const getProjectsCount = async () => {
  const response = await api({
    method: 'GET',
    url: `${RESOURCE.PROJECTS}/count`,
  });

  return response;
};

export const createSpeaker = async (data) => {
  const response = await api({
    method: 'POST',
    url: RESOURCE.SPEAKERS,
    data,
  });
  return response;
};

export const updateSpeaker = async (speakerId, data) => {
  const response = await api({
    method: 'PUT',
    url: `${RESOURCE.SPEAKERS}/${speakerId}`,
    data,
  });
  return response;
};

export const deleteSpeaker = async (speakerId) => {
  const response = await api({
    method: 'DELETE',
    url: `${RESOURCE.SPEAKERS}/${speakerId}`,
  });
  return response;
};

export const getSpeakers = async (projectId) => {
  const response = await api({
    method: 'GET',
    url: RESOURCE.SPEAKERS,
    params: { projectId },
  });
  return response;
};

export const synthesisProject = async (
  projectId,
  { recaptchaToken, turnstileToken },
) => {
  const response = await api({
    method: 'POST',
    url: `${RESOURCE.PROJECTS}/${projectId}/synthesis`,
    headers: {
      'recaptcha-token': recaptchaToken || '',
      'turnstile-token': turnstileToken || '',
    },
  });

  return response;
};

export const translateProject = async ({
  projectId,
  sourceLanguage,
  targetLanguage,
  webhookUrl,
  blocks,
}) => {
  const requestData = {
    projectId,
    targetLanguage,
    webhookUrl,
    blocks,
  };

  // Only include sourceLanguage if it's not 'auto'
  if (sourceLanguage && sourceLanguage !== 'auto') {
    requestData.sourceLanguage = sourceLanguage;
  }

  const response = await api({
    method: 'POST',
    baseURL: `${DUBBING_URL}/api/v1`,
    url: RESOURCE.TRANSLATE,
    data: requestData,
  });

  return response;
};

export const getTranslateProgress = async (projectId) => {
  const response = await api({
    method: 'GET',
    url: `${RESOURCE.PROJECTS}/${projectId}/translate-progress`,
  });

  return response;
};

export const getRewriteProgress = async (projectId) => {
  const response = await api({
    method: 'GET',
    url: `${RESOURCE.PROJECTS}/${projectId}/rewrite-progress`,
  });

  return response;
};

export const getSourcePreparationProgress = async (projectId) => {
  const response = await api({
    method: 'GET',
    url: `${RESOURCE.PROJECTS}/${projectId}/source/preparation`,
  });

  return response;
};

export const exportProject = async (
  projectId,
  type = EXPORT_TYPE.PPTX,
  videoExportEngine,
  blockIds,
) => {
  const response = await api({
    method: 'POST',
    url: `${RESOURCE.PROJECTS}/${projectId}/export`,
    data: {
      type,
      ...(videoExportEngine && { videoExportEngine }),
      ...(blockIds && { blockIds }),
    },
  });

  return response;
};

export const getExportProgress = async (projectId, type = EXPORT_TYPE.PPTX) => {
  const response = await api({
    method: 'GET',
    url: `${RESOURCE.PROJECTS}/${projectId}/export-progress?type=${type}`,
  });

  return response;
};

/**
 * Progress of one export record, keyed by the record's own id.
 *
 * Replaces `getExportProgress` for the flow the backend documents now: that one
 * keys on the export type, so it answers for whichever record the backend
 * happens to consider current for that type, and it rejects `type=SRT`. The
 * record — status, `fileUrl`, `fileName`, `fileExpiresAt` — comes back under
 * `result`, not under `result.progress`.
 */
export const getProjectExportProgress = async (projectId, exportId) => {
  const response = await api({
    method: 'GET',
    url: `${RESOURCE.PROJECTS}/${projectId}/exports/${exportId}/progress`,
  });

  return response;
};

/**
 * Export records of a project, newest first.
 *
 * Paged by an opaque `cursor` and `limit`, the contract the backend exposes.
 * The cursor is `pagination.nextCursor` of the previous response, passed back
 * verbatim; leaving it out asks for the first page.
 */
export const getProjectExports = async (
  projectId,
  { cursor, limit, signal } = {},
) => {
  const params = omitIsNil({ cursor, limit });

  const response = await api({
    method: 'GET',
    url: `${RESOURCE.PROJECTS}/${projectId}/exports`,
    params,
    signal,
  });

  return response;
};

export const rewriteProject = async (data) => {
  const response = await api({
    method: 'POST',
    baseURL: `${DUBBING_URL}/api/v1`,
    url: `${RESOURCE.REWRITE}`,
    data,
  });

  return response;
};

export const deleteTranslation = async (projectId) => {
  const response = await api({
    method: 'DELETE',
    url: `${RESOURCE.PROJECTS}/${projectId}/translation`,
  });

  return response;
};

export const deleteBlockRewrite = async (projectId, blockId) => {
  const response = await api({
    method: 'DELETE',
    url: `${RESOURCE.PROJECTS}/${projectId}/blocks/${blockId}/rewrite`,
  });

  return response;
};

export const deleteProjectRewrite = async (projectId) => {
  const response = await api({
    method: 'DELETE',
    url: `${RESOURCE.PROJECTS}/${projectId}/rewrite`,
  });

  return response;
};

export const generateScripts = async (projectId, data) => {
  const response = await api({
    method: 'POST',
    url: `${RESOURCE.PROJECTS}/${projectId}/generate-scripts`,
    data,
  });

  return response;
};

export const patchProjectBlocks = async (projectId, blocks) => {
  const response = await api({
    method: 'PATCH',
    url: `${RESOURCE.PROJECTS}/${projectId}/blocks`,
    data: { blocks },
  });

  return response;
};

export const patchProjectConfig = async (projectId, data) => {
  const response = await api({
    method: 'PATCH',
    url: `${RESOURCE.PROJECTS}/${projectId}`,
    data,
  });

  return response;
};

export const deleteProjectBlocks = async (projectId, blockIds) => {
  const response = await api({
    method: 'DELETE',
    url: `${RESOURCE.PROJECTS}/${projectId}/blocks`,
    data: { blockIds },
  });

  return response;
};

export const patchProjectBlockOrder = async (projectId, blockOrder) => {
  const response = await api({
    method: 'PATCH',
    url: `${RESOURCE.PROJECTS}/${projectId}/blocks/order`,
    data: { blockOrder },
  });

  return response;
};

export const addProjectBlocks = async (projectId, { blocks, afterBlockId }) => {
  const response = await api({
    method: 'POST',
    url: `${RESOURCE.PROJECTS}/${projectId}/blocks`,
    data: omitIsNil({ afterBlockId, blocks }),
  });

  return response;
};
